import {OrganizationStructure} from "./organization.structure";

/**
	* ***************************************************************
	*  Written by: Dana Moshe
	* ***************************************************************
	*/
export interface Node {
	data:OrganizationStructure.OrgStructure;
	items?:Node[];
}

export class OrganizationStructureTree {
	root:Node;
	data:Array<OrganizationStructure.OrgStructure>;

	/**
	 * Get a subtree by organization structure key.
	 * @param orgStructureKey key of orgStructure to search.
	 * @param root the starting point of the search.
	 * @returns a node that is a subtree of the root node.
	 */
	public static getOrganizationStructureByKey(orgStructureKey:number, root:Node):Node {

		// If the key found.
		if (root.data.orgStructureKey === orgStructureKey) {
			return root;
		}

		// If there are children.
		if (root.items) {
			for (let item of root.items.sort((a, b) => a.data.description.localeCompare(b.data.description))) {
				let node = this.getOrganizationStructureByKey(orgStructureKey, item);

				// If the key found.
				if (node) {
					return node;
				}
			}
		}
		return;
	}

	private static isOrgStructureDescendantOfOrgStructureNode(descendantOrgStructureKey:number,root:Node):boolean {

		// If the key found.
		if (root.data.orgStructureKey === descendantOrgStructureKey) {
			return true;
		}

		// If there are children.
		if (root.items) {
			// for (let item of root.items) {
			// 	if (item.data.orgStructureKey === descendantOrgStructureKey) {
			// 		return true;
			// 	}
			// }

			for (let item of root.items) {
				if (this.isOrgStructureDescendantOfOrgStructureNode(descendantOrgStructureKey, item)) {
					return true;
				}
			}
		}
		return false;
	}

	public isOrgStructureDescendantOfOrgStructure(descendantOrgStructureKey:number, parentOrgStructureKey:number):boolean {
		let node = OrganizationStructureTree.getOrganizationStructureByKey(parentOrgStructureKey, this.root);
		return OrganizationStructureTree.isOrgStructureDescendantOfOrgStructureNode(descendantOrgStructureKey,node);
	}

	public getOrganizationStructureDataByKey(orgStructureKey:number): OrganizationStructure.OrgStructure {
		let node = OrganizationStructureTree.getOrganizationStructureByKey(orgStructureKey, this.root);
		return node ? node.data : null;
	}

	constructor(data:OrganizationStructure.OrgStructure[]) {
		this.data = data;

		//Search the root row
		for (let i = 0; i < data.length; i++) {
			// If find the root
			if (data[i].parentOrgStructureKey == null) {
				this.root = {
					data: this.getOrgStructure(data[i]),
					items: this.getChildren(data, data[i].orgStructureKey),
				};
				break;
			}
		}
	}

	public getEffectiveAddress(orgStructKey:number): OrganizationStructure.OrgStructure {
		let currentKey = orgStructKey;
		let currentNode = OrganizationStructureTree.getOrganizationStructureByKey(currentKey, this.root);
		while (currentNode != null && currentNode.data.useParentLocation === "Y") {
			currentKey = currentNode.data.parentOrgStructureKey;
			currentNode = currentKey ? OrganizationStructureTree.getOrganizationStructureByKey(currentKey,this.root) : null;
		}
		return currentNode ? currentNode.data : null;
	}

	/**
		* Return an array of children nodes by key.
		*/
	public getChildren(data:OrganizationStructure.OrgStructure[], key:number):Node[] {

		let children:Node[] = [];

		for (let i = 0; i < data.length; i++) {
			if (data[i].parentOrgStructureKey === key) {
				let orgStructure = this.getOrgStructure(data[i]);
				let child:Node = {
					data: orgStructure,
					items: this.getChildren(data, data[i].orgStructureKey),
				};

				// If the child is a leaf then remove item field.
				if (child.items.length === 0) {
					child = {
						data: orgStructure,
					};
				}
				children.push(child);
			}
		}
		return children;
	}

	/**
		* Set the dataRow of OrgStructure that sent from the server
		* to the structure that defined in the client.
		*/
	public getOrgStructure(data:OrganizationStructure.OrgStructure):OrganizationStructure.OrgStructure {
		data.address = data.address?Object.assign({},data.address):{
			floor: data["floor"] ,
			building:  data["building"] ,
			streetAddress: data["streetAddress"] ,
			otherDestination: data["otherDestination"] ,
			city: data["city"] ,
			province: data["province"] ,
			country: data["country"] ,
			postalCode:  data["postalCode"],
		};

		if (!Object.keys(data.address).some(x=>data.address[x]!=null)){
			data.address=null;
		}
		delete data["floor"];
		delete data["building"];
		delete data["streetAddress"];
		delete data["otherDestination"];
		delete data["city"];
		delete data["province"];
		delete data["country"];
		delete data["postalCode"];

		return data;
	}
}
